import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { CreditCard, Plus, Edit2, Trash2, X, Check, Crown, Zap, Star, AlertTriangle, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    price: 0,
    duration_days: 30,
    max_sessions: 1,
    rate_limit_per_minute: 0,
    rate_limit_per_hour: 0,
    rate_limit_per_day: 0,
    api_access: false,
    features: [''],
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/subscription/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setMessage({ type: 'error', text: 'Failed to load plans' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalType('create');
    setFormData({
      name: '',
      slug: '',
      price: 0,
      duration_days: 30,
      max_sessions: 1,
      rate_limit_per_minute: 0,
      rate_limit_per_hour: 0,
      rate_limit_per_day: 0,
      api_access: false,
      features: [''],
      is_active: true
    });
    setSelectedPlan(null);
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setModalType('edit');
    setSelectedPlan(plan);
    setFormData({
      name: plan.name,
      slug: plan.slug,
      price: plan.price,
      duration_days: plan.duration_days || 30,
      max_sessions: plan.max_sessions,
      rate_limit_per_minute: plan.rate_limit_per_minute,
      rate_limit_per_hour: plan.rate_limit_per_hour,
      rate_limit_per_day: plan.rate_limit_per_day,
      api_access: plan.api_access,
      features: plan.features?.length > 0 ? plan.features : [''],
      is_active: plan.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        features: formData.features.filter(f => f.trim() !== '')
      };

      if (modalType === 'create') {
        await axios.post(`${API}/admin/subscription/plans`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Plan created successfully!' });
      } else {
        await axios.put(`${API}/admin/subscription/plans/${selectedPlan.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Plan updated successfully!' });
      }

      setShowModal(false);
      fetchPlans();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Operation failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (plan.slug === 'free') {
      alert('Cannot delete the Free plan');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${plan.name}" plan?`)) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.delete(`${API}/admin/subscription/plans/${plan.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: response.data.message });
      fetchPlans();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Delete failed' });
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures.length > 0 ? newFeatures : [''] });
  };

  const getPlanIcon = (slug) => {
    switch (slug) {
      case 'free': return <Star size={20} />;
      case 'basic': return <Zap size={20} />;
      case 'pro': return <Crown size={20} />;
      default: return <CreditCard size={20} />;
    }
  };

  const getPlanColor = (slug) => {
    switch (slug) {
      case 'free': return { bg: '#f0fdf4', border: '#86efac', icon: '#22c55e' };
      case 'basic': return { bg: '#eff6ff', border: '#93c5fd', icon: '#3b82f6' };
      case 'pro': return { bg: '#fef3c7', border: '#fcd34d', icon: '#f59e0b' };
      default: return { bg: '#f8fafc', border: '#e2e8f0', icon: '#64748b' };
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ color: '#64748b', padding: '40px', textAlign: 'center' }}>Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Subscription Plans</h1>
          <p style={{ color: '#64748b', fontSize: '15px' }}>Manage subscription plans for your users</p>
        </div>
        <button
          onClick={handleCreate}
          data-testid="create-plan-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          <Plus size={20} />
          Create Plan
        </button>
      </div>

      {message.text && (
        <div style={{
          padding: '12px 16px',
          background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#991b1b',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {message.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
          {message.text}
        </div>
      )}

      {/* Plans Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
        {plans.map((plan) => {
          const colors = getPlanColor(plan.slug);
          return (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.slug}`}
              style={{
                background: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: `2px solid ${plan.is_active ? colors.border : '#fee2e2'}`,
                opacity: plan.is_active ? 1 : 0.7
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px',
                background: colors.bg,
                borderBottom: `1px solid ${colors.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colors.icon,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    {getPlanIcon(plan.slug)}
                  </div>
                  <div>
                    <h3 style={{ color: '#1e293b', fontSize: '18px', fontWeight: '700', margin: 0 }}>
                      {plan.name}
                    </h3>
                    <span style={{
                      fontSize: '12px',
                      color: '#64748b',
                      background: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontFamily: 'monospace'
                    }}>
                      {plan.slug}
                    </span>
                  </div>
                </div>
                {!plan.is_active && (
                  <span style={{
                    fontSize: '11px',
                    padding: '4px 8px',
                    background: '#fee2e2',
                    color: '#991b1b',
                    borderRadius: '4px',
                    fontWeight: '600'
                  }}>
                    INACTIVE
                  </span>
                )}
              </div>

              {/* Price */}
              <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                  <span style={{ color: '#94a3b8', fontSize: '20px' }}>₹</span>
                  <span style={{ color: '#1e293b', fontSize: '36px', fontWeight: '700' }}>{plan.price}</span>
                  {plan.duration_days > 0 && (
                    <span style={{ color: '#64748b', fontSize: '14px' }}>/ {plan.duration_days} days</span>
                  )}
                  {plan.duration_days === 0 && (
                    <span style={{ color: '#64748b', fontSize: '14px' }}>/ forever</span>
                  )}
                </div>
              </div>

              {/* Limits */}
              <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9' }}>
                <h4 style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '12px' }}>
                  Limits
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 4px 0' }}>Sessions</p>
                    <p style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600', margin: 0 }}>
                      {plan.max_sessions}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 4px 0' }}>API Access</p>
                    <p style={{ color: plan.api_access ? '#22c55e' : '#ef4444', fontSize: '16px', fontWeight: '600', margin: 0 }}>
                      {plan.api_access ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 4px 0' }}>Per Minute</p>
                    <p style={{ color: '#1e293b', fontSize: '14px', fontWeight: '500', margin: 0 }}>
                      {plan.rate_limit_per_minute === 0 ? 'Unlimited' : plan.rate_limit_per_minute}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 4px 0' }}>Per Hour</p>
                    <p style={{ color: '#1e293b', fontSize: '14px', fontWeight: '500', margin: 0 }}>
                      {plan.rate_limit_per_hour === 0 ? 'Unlimited' : plan.rate_limit_per_hour}
                    </p>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 4px 0' }}>Per Day</p>
                    <p style={{ color: '#1e293b', fontSize: '14px', fontWeight: '500', margin: 0 }}>
                      {plan.rate_limit_per_day === 0 ? 'Unlimited' : plan.rate_limit_per_day}
                    </p>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9' }}>
                <h4 style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '12px' }}>
                  Features
                </h4>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {(plan.features || []).map((feature, i) => (
                    <li key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 0',
                      color: '#475569',
                      fontSize: '14px'
                    }}>
                      <Check size={16} style={{ color: '#22c55e' }} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actions */}
              <div style={{ padding: '16px 20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => handleEdit(plan)}
                  data-testid={`edit-plan-${plan.slug}`}
                  style={{
                    padding: '8px 16px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: '500'
                  }}
                >
                  <Edit2 size={16} />
                  Edit
                </button>
                {plan.slug !== 'free' && (
                  <button
                    onClick={() => handleDelete(plan)}
                    data-testid={`delete-plan-${plan.slug}`}
                    style={{
                      padding: '8px 16px',
                      background: '#fee2e2',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      color: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: '500'
                    }}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
                {modalType === 'create' ? 'Create Plan' : 'Edit Plan'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                    Plan Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Premium"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      color: '#1e293b',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                    Slug * {modalType === 'edit' && <span style={{ color: '#94a3b8', fontSize: '12px' }}>(read-only)</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    required
                    disabled={modalType === 'edit'}
                    placeholder="e.g., premium"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      color: '#1e293b',
                      boxSizing: 'border-box',
                      background: modalType === 'edit' ? '#f8fafc' : 'white'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                    Price (₹)
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      color: '#1e293b',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                    Duration (days) <span style={{ color: '#94a3b8', fontSize: '12px' }}>0 = no expiry</span>
                  </label>
                  <input
                    type="number"
                    value={formData.duration_days}
                    onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 0 })}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      color: '#1e293b',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                    Max Sessions
                  </label>
                  <input
                    type="number"
                    value={formData.max_sessions}
                    onChange={(e) => setFormData({ ...formData, max_sessions: parseInt(e.target.value) || 1 })}
                    min="1"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      color: '#1e293b',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                    API Access
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, api_access: !formData.api_access })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      background: formData.api_access ? '#dcfce7' : '#fee2e2',
                      color: formData.api_access ? '#166534' : '#991b1b',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {formData.api_access ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                  Rate Limits <span style={{ color: '#94a3b8', fontSize: '12px' }}>(0 = Unlimited)</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', color: '#94a3b8', fontSize: '12px' }}>Per Minute</label>
                    <input
                      type="number"
                      value={formData.rate_limit_per_minute}
                      onChange={(e) => setFormData({ ...formData, rate_limit_per_minute: parseInt(e.target.value) || 0 })}
                      min="0"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        color: '#1e293b',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', color: '#94a3b8', fontSize: '12px' }}>Per Hour</label>
                    <input
                      type="number"
                      value={formData.rate_limit_per_hour}
                      onChange={(e) => setFormData({ ...formData, rate_limit_per_hour: parseInt(e.target.value) || 0 })}
                      min="0"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        color: '#1e293b',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', color: '#94a3b8', fontSize: '12px' }}>Per Day</label>
                    <input
                      type="number"
                      value={formData.rate_limit_per_day}
                      onChange={(e) => setFormData({ ...formData, rate_limit_per_day: parseInt(e.target.value) || 0 })}
                      min="0"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        color: '#1e293b',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                  Features
                </label>
                {formData.features.map((feature, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      placeholder={`Feature ${index + 1}`}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        color: '#1e293b'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      style={{
                        padding: '10px',
                        background: '#fee2e2',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: '#ef4444'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFeature}
                  style={{
                    padding: '8px 16px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#64748b',
                    fontSize: '14px'
                  }}
                >
                  + Add Feature
                </button>
              </div>

              {modalType === 'edit' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>Active Plan</span>
                    <span style={{ color: '#94a3b8', fontSize: '12px' }}>
                      (Inactive plans won't be available for new subscriptions)
                    </span>
                  </label>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '12px 24px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#64748b',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '12px 24px',
                    background: '#667eea',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    opacity: saving ? 0.6 : 1
                  }}
                >
                  {saving ? 'Saving...' : modalType === 'create' ? 'Create Plan' : 'Update Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
